import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { usePager, Pager } from "@/components/kiyana/Pager";
import { api } from "@/lib/api";
import { formatIDR, formatDateID } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, X, Search, RefreshCw, Power, ChevronRight, ChevronDown } from "lucide-react";
import { MAROON, inpL } from "@/components/kiyana/LeaveForm";

const card = "bg-white rounded-2xl border border-slate-100";
const SYNC_LABEL = { draft: "Draft", tersinkron: "Tersinkron", belum_tersinkron: "Belum Tersinkron" };
const SYNC_CHIP = { draft: "bg-slate-100 text-slate-500", tersinkron: "bg-emerald-50 text-emerald-600", belum_tersinkron: "bg-red-50 text-red-600" };

function Modal({ tid, title, onClose, children, wide }) {
  return (
    <div data-testid={tid} className="fixed inset-0 z-50 bg-slate-900/40 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <div className={`bg-white w-full ${wide ? "sm:max-w-2xl" : "sm:max-w-lg"} rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <h3 className="font-display text-base font-bold text-slate-900">{title}</h3>
          <button data-testid={`${tid}-close`} onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-50 flex items-center justify-center text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">{children}</div>
      </div>
    </div>
  );
}

const emptyForm = { name: "", product_id: "", variant: "", variants: [], materials: [], cost_potong: "", cost_jahit: "", cost_finishing: "", consumables: [], extras: [], status: "aktif", note: "" };

function calcLocal(f) {
  const fabric = f.materials.reduce((s, m) => s + Math.round((parseFloat(m.usage_per_pcs) || 0) * (parseInt(m.price_per_unit) || 0)), 0);
  const cons = f.consumables.reduce((s, c) => s + Math.round((parseFloat(c.qty) || 0) * (parseInt(c.price_per_unit) || 0)), 0);
  const extra = f.extras.reduce((s, e) => s + (parseInt(e.amount) || 0), 0);
  const ongkos = (parseInt(f.cost_potong) || 0) + (parseInt(f.cost_jahit) || 0) + (parseInt(f.cost_finishing) || 0);
  return { fabric, cons, extra, ongkos, total: fabric + cons + extra + ongkos };
}

function CostForm({ initial, options, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(isEdit ? {
    name: initial.name, product_id: initial.product_id, variant: initial.variant,
    materials: initial.materials.map((m) => ({ material_id: m.material_id, usage_per_pcs: String(m.usage_per_pcs), price_per_unit: String(m.price_per_unit) })),
    cost_potong: String(initial.cost_potong || ""), cost_jahit: String(initial.cost_jahit || ""), cost_finishing: String(initial.cost_finishing || ""),
    consumables: initial.consumables.map((c) => ({ name: c.name, qty: String(c.qty), unit: c.unit, price_per_unit: String(c.price_per_unit) })),
    extras: initial.extras.map((e) => ({ name: e.name, amount: String(e.amount) })),
    status: initial.status, note: initial.note || "",
  } : emptyForm);
  const [prodQ, setProdQ] = useState("");
  const [prodOpen, setProdOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const prod = options.products.find((p) => p.id === form.product_id);
  const prodOpts = options.products.filter((p) => p.name.toLowerCase().includes(prodQ.toLowerCase()));
  const t = calcLocal(form);
  const selVariant = isEdit ? form.variant : (form.variants.length === 1 ? form.variants[0] : "");
  const currentCost = prod && selVariant ? (prod.variants.find((v) => v.label === selVariant)?.cost_price ?? prod.cost_price ?? 0) : null;

  const payload = () => ({
    name: form.name.trim(), product_id: form.product_id, variant: form.variant,
    materials: form.materials.filter((m) => m.material_id && parseFloat(m.usage_per_pcs) > 0).map((m) => ({ material_id: m.material_id, usage_per_pcs: parseFloat(m.usage_per_pcs), price_per_unit: parseInt(m.price_per_unit) || 0 })),
    cost_potong: parseInt(form.cost_potong) || 0, cost_jahit: parseInt(form.cost_jahit) || 0, cost_finishing: parseInt(form.cost_finishing) || 0,
    consumables: form.consumables.filter((c) => c.name.trim() && parseInt(c.price_per_unit) >= 0 && parseFloat(c.qty) > 0).map((c) => ({ name: c.name.trim(), qty: parseFloat(c.qty), unit: c.unit, price_per_unit: parseInt(c.price_per_unit) || 0 })),
    extras: form.extras.filter((e) => e.name.trim() && parseInt(e.amount) > 0).map((e) => ({ name: e.name.trim(), amount: parseInt(e.amount) })),
    status: form.status, note: form.note,
  });

  const validate = () => {
    if (form.name.trim().length < 3) { toast.error("Nama produk produksi wajib diisi"); return false; }
    if (!form.product_id) { toast.error("Pilih Produk Utama yang terhubung"); return false; }
    if (isEdit ? !form.variant : !form.variants.length) { toast.error("Pilih minimal 1 variasi yang terhubung"); return false; }
    if (t.total <= 0) { toast.error("Total HPP harus lebih dari 0"); return false; }
    return true;
  };

  const saveDraft = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      if (isEdit) {
        await api.put(`/admin/prod/costs/${initial.id}`, payload());
        toast.success("Tersimpan sebagai draft — Harga Beli Produk Utama TIDAK diubah");
      } else {
        const { variant, ...rest } = payload();
        const r = await api.post("/admin/prod/costs/bulk", { ...rest, variants: form.variants, sync: false });
        const sk = r.data.skipped.length;
        toast.success(`${r.data.created.length} variasi tersimpan sebagai draft — Harga Beli TIDAK diubah` + (sk ? ` • ${sk} dilewati` : ""));
      }
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); } finally { setBusy(false); }
  };

  const saveAndSync = () => {
    if (!validate()) return;
    const labels = isEdit ? [form.variant] : form.variants;
    setConfirm({ product: prod, newPrice: t.total,
      variants: labels.map((label) => ({ label, oldPrice: prod ? (prod.variants.find((v) => v.label === label)?.cost_price ?? prod.cost_price ?? 0) : 0 })) });
  };

  const doSync = async () => {
    setBusy(true);
    try {
      if (isEdit) {
        await api.put(`/admin/prod/costs/${initial.id}`, payload());
        await api.post(`/admin/prod/costs/${initial.id}/sync`);
        toast.success(`Harga Beli diperbarui: ${formatIDR(confirm.variants[0].oldPrice)} → ${formatIDR(confirm.newPrice)}`);
      } else {
        const { variant, ...rest } = payload();
        const r = await api.post("/admin/prod/costs/bulk", { ...rest, variants: form.variants, sync: true });
        const synced = r.data.created.filter((c) => c.sync?.ok).length;
        const sk = r.data.skipped.length;
        toast.success(`${synced} variasi tersimpan & Harga Beli → ${formatIDR(r.data.total_hpp)}` + (sk ? ` • ${sk} dilewati/gagal sinkron` : ""));
      }
      onSaved?.(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sinkronisasi gagal — status disimpan Belum Tersinkron, gunakan Coba Lagi");
      onSaved?.(); onClose();
    } finally { setBusy(false); }
  };

  const setRow = (key, i, patch) => setForm((f) => { const arr = [...f[key]]; arr[i] = { ...arr[i], ...patch }; return { ...f, [key]: arr }; });
  const delRow = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, j) => j !== i) }));

  return (
    <Modal tid="costf-modal" title={isEdit ? `Edit ${initial.code}` : "Tambah Produk Produksi"} onClose={onClose} wide>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-semibold text-slate-500">Nama Produk Produksi <span className="text-red-500">*</span></label>
          <input data-testid="costf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inpL} placeholder="mis. Outer Megamendung Jahit Dalam" />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-slate-500">Status</label>
          <select data-testid="costf-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inpL}>
            <option value="aktif">Aktif</option><option value="nonaktif">Nonaktif</option>
          </select>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-semibold text-slate-500">Produk Utama Terhubung <span className="text-red-500">*</span></label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="costf-prod-q" value={prodOpen || !form.product_id ? prodQ : (prod?.name || "")}
              onFocus={() => { setProdQ(""); setForm((f) => ({ ...f, product_id: "", variant: "", variants: [] })); setProdOpen(true); }}
              onClick={() => { setProdQ(""); setForm((f) => ({ ...f, product_id: "", variant: "", variants: [] })); setProdOpen(true); }}
              onBlur={() => setTimeout(() => setProdOpen(false), 250)}
              onChange={(e) => { setProdQ(e.target.value); setForm({ ...form, product_id: "", variant: "", variants: [] }); setProdOpen(true); }}
              placeholder="Klik di sini — daftar produk langsung muncul" className={`${inpL} pl-9 pr-8`} />
            <ChevronDown data-testid="costf-prod-chevron" className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
          {prodOpen && !form.product_id && (
            <div data-testid="costf-prod-list" className="mt-1 border-2 border-[#7F1D2D]/30 rounded-xl overflow-hidden max-h-56 overflow-y-auto bg-white shadow-lg">
              {options.products.length === 0 && <p className="px-3 py-3 text-[10px] text-slate-400">Memuat database produk...</p>}
              {prodOpts.slice(0, 20).map((p) => (
                <button key={p.id} data-testid={`costf-prod-${p.id}`}
                  onMouseDown={(e) => { e.preventDefault(); setForm({ ...form, product_id: p.id, variant: p.variants?.length === 1 ? p.variants[0].label : "", variants: p.variants?.length === 1 ? [p.variants[0].label] : [], name: form.name.trim() ? form.name : p.name }); setProdOpen(false); }}
                  onTouchStart={(e) => { e.preventDefault(); setForm({ ...form, product_id: p.id, variant: p.variants?.length === 1 ? p.variants[0].label : "", variants: p.variants?.length === 1 ? [p.variants[0].label] : [], name: form.name.trim() ? form.name : p.name }); setProdOpen(false); }}
                  className="w-full text-left px-3 py-2.5 text-xs hover:bg-rose-50/40 active:bg-rose-50 text-slate-700 border-b border-slate-50 last:border-0">{p.name} <span className="text-slate-400">• Harga Beli {formatIDR(p.cost_price || 0)}</span></button>
              ))}
              {options.products.length > 0 && !prodOpts.length && <p className="px-3 py-3 text-[10px] text-slate-400">Produk tidak ditemukan — gunakan produk yang sudah ada, jangan buat ulang.</p>}
            </div>
          )}
        </div>
        <div>
          <label className="text-[11px] font-semibold text-slate-500">Variasi Terhubung <span className="text-red-500">*</span>{!isEdit && prod && (prod.variants || []).length > 1 && <span className="ml-1 font-normal text-slate-400 normal-case">(bisa pilih banyak sekaligus)</span>}</label>
          {isEdit ? (
            <select data-testid="costf-variant" value={form.variant} onChange={(e) => setForm({ ...form, variant: e.target.value })} className={inpL}>
              <option value="">Pilih variasi</option>
              {(prod?.variants || []).map((v) => <option key={v.sku || v.label} value={v.label}>{`${v.label} — Harga Beli ${formatIDR(v.cost_price ?? 0)}`}</option>)}
            </select>
          ) : (
            <div data-testid="costf-variant-multi" className="border border-slate-200 rounded-xl max-h-36 overflow-y-auto">
              {!prod && <p className="px-3 py-2.5 text-[10px] text-slate-400">Pilih produk utama terlebih dahulu</p>}
              {prod && (prod.variants || []).map((v, i) => (
                <label key={v.sku || v.label} className="flex items-center gap-2 px-3 py-2 text-xs border-b border-slate-50 last:border-0 hover:bg-rose-50/40 cursor-pointer">
                  <input type="checkbox" data-testid={`costf-variant-check-${i}`} checked={form.variants.includes(v.label)}
                    onChange={(e) => setForm((f) => ({ ...f, variants: e.target.checked ? [...f.variants, v.label] : f.variants.filter((x) => x !== v.label) }))}
                    className="accent-[#7F1D2D] w-3.5 h-3.5 shrink-0" />
                  <span className="flex-1 text-slate-700">{v.label}</span>
                  <span className="text-[10px] text-slate-400">Harga Beli {formatIDR(v.cost_price ?? 0)}</span>
                </label>
              ))}
              {prod && (prod.variants || []).length > 1 && (
                <button type="button" data-testid="costf-variant-all"
                  onClick={() => setForm((f) => ({ ...f, variants: f.variants.length === prod.variants.length ? [] : prod.variants.map((v) => v.label) }))}
                  className="w-full px-3 py-2 text-[10px] font-semibold text-[#7F1D2D] text-left bg-rose-50/40 sticky bottom-0">
                  {form.variants.length === prod.variants.length ? "Hapus Semua Pilihan" : `Pilih Semua Variasi (${prod.variants.length})`}
                </button>
              )}
            </div>
          )}
          {!isEdit && form.variants.length > 0 && <p data-testid="costf-variant-count" className="text-[10px] text-[#7F1D2D] font-semibold mt-1">{form.variants.length} variasi dipilih — masing-masing dibuatkan master HPP terpisah</p>}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[11px] font-semibold text-slate-500">Rincian Bahan Kain (per pcs)</label>
          <button data-testid="costf-add-mat" onClick={() => setForm((f) => ({ ...f, materials: [...f.materials, { material_id: "", usage_per_pcs: "", price_per_unit: "" }] }))} className="text-[11px] font-semibold text-[#7F1D2D] flex items-center gap-1"><Plus className="w-3 h-3" /> Bahan</button>
        </div>
        {form.materials.map((m, i) => {
          const mat = options.materials.find((x) => x.id === m.material_id);
          return (
            <div key={i} className="flex gap-1.5 mb-1.5 items-center">
              <select data-testid={`costf-mat-${i}`} value={m.material_id} onChange={(e) => setRow("materials", i, { material_id: e.target.value })} className={inpL}>
                <option value="">Bahan</option>
                {options.materials.map((x) => <option key={x.id} value={x.id}>{`${x.name} (${x.unit})`}</option>)}
              </select>
              <input data-testid={`costf-mat-usage-${i}`} type="number" step="0.1" min="0" value={m.usage_per_pcs} placeholder="Pakai" onChange={(e) => setRow("materials", i, { usage_per_pcs: e.target.value })} className="h-10 w-20 px-2 rounded-xl border border-slate-200 text-xs" />
              <span className="text-[10px] text-slate-400 w-8">{mat?.unit || ""}</span>
              <input data-testid={`costf-mat-price-${i}`} type="number" min="0" value={m.price_per_unit} placeholder="Harga/satuan" onChange={(e) => setRow("materials", i, { price_per_unit: e.target.value })} className="h-10 w-24 px-2 rounded-xl border border-slate-200 text-xs" />
              <span className="text-[10px] font-bold text-slate-600 w-16 text-right">{formatIDR(Math.round((parseFloat(m.usage_per_pcs) || 0) * (parseInt(m.price_per_unit) || 0)))}</span>
              <button onClick={() => delRow("materials", i)} className="text-slate-300 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-3 gap-2">
        {[["cost_potong", "Ongkos Potong/pcs", "costf-potong"], ["cost_jahit", "Ongkos Jahit/pcs", "costf-jahit"], ["cost_finishing", "Ongkos Finishing/pcs", "costf-finishing"]].map(([k, lb, tid]) => (
          <div key={k}><label className="text-[11px] font-semibold text-slate-500">{lb}</label>
            <input data-testid={tid} type="number" min="0" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={inpL} placeholder="0" /></div>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[11px] font-semibold text-slate-500">Consumable / Aksesoris</label>
          <button data-testid="costf-add-cons" onClick={() => setForm((f) => ({ ...f, consumables: [...f.consumables, { name: "", qty: "1", unit: "pcs", price_per_unit: "" }] }))} className="text-[11px] font-semibold text-[#7F1D2D] flex items-center gap-1"><Plus className="w-3 h-3" /> Komponen</button>
        </div>
        {form.consumables.map((c, i) => (
          <div key={i} className="flex gap-1.5 mb-1.5 items-center">
            <input data-testid={`costf-cons-name-${i}`} value={c.name} placeholder="Nama komponen" onChange={(e) => setRow("consumables", i, { name: e.target.value })} className={inpL} />
            <input data-testid={`costf-cons-qty-${i}`} type="number" step="0.1" min="0" value={c.qty} placeholder="Qty" onChange={(e) => setRow("consumables", i, { qty: e.target.value })} className="h-10 w-16 px-2 rounded-xl border border-slate-200 text-xs" />
            <select data-testid={`costf-cons-unit-${i}`} value={c.unit} onChange={(e) => setRow("consumables", i, { unit: e.target.value })} className="h-10 w-20 px-2 rounded-xl border border-slate-200 text-xs">
              {options.units.map((u) => <option key={u.id} value={u.label}>{u.label}</option>)}
            </select>
            <input data-testid={`costf-cons-price-${i}`} type="number" min="0" value={c.price_per_unit} placeholder="Harga" onChange={(e) => setRow("consumables", i, { price_per_unit: e.target.value })} className="h-10 w-24 px-2 rounded-xl border border-slate-200 text-xs" />
            <button onClick={() => delRow("consumables", i)} className="text-slate-300 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[11px] font-semibold text-slate-500">Biaya Tambahan (opsional, per pcs)</label>
          <button data-testid="costf-add-extra" onClick={() => setForm((f) => ({ ...f, extras: [...f.extras, { name: "", amount: "" }] }))} className="text-[11px] font-semibold text-[#7F1D2D] flex items-center gap-1"><Plus className="w-3 h-3" /> Biaya</button>
        </div>
        {form.extras.map((e, i) => (
          <div key={i} className="flex gap-1.5 mb-1.5 items-center">
            <input data-testid={`costf-extra-name-${i}`} value={e.name} placeholder="mis. Packaging, transport" onChange={(e2) => setRow("extras", i, { name: e2.target.value })} className={inpL} />
            <input data-testid={`costf-extra-amount-${i}`} type="number" min="0" value={e.amount} placeholder="Nominal" onChange={(e2) => setRow("extras", i, { amount: e2.target.value })} className="h-10 w-28 px-2 rounded-xl border border-slate-200 text-xs" />
            <button onClick={() => delRow("extras", i)} className="text-slate-300 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>

      <div><label className="text-[11px] font-semibold text-slate-500">Catatan</label>
        <input data-testid="costf-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inpL} /></div>

      <div data-testid="costf-total" className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-xs space-y-1">
        <div className="flex justify-between"><span className="text-slate-500">Biaya kain</span><b>{formatIDR(t.fabric)}</b></div>
        <div className="flex justify-between"><span className="text-slate-500">Ongkos potong + jahit + finishing</span><b>{formatIDR(t.ongkos)}</b></div>
        <div className="flex justify-between"><span className="text-slate-500">Consumable/aksesoris</span><b>{formatIDR(t.cons)}</b></div>
        <div className="flex justify-between"><span className="text-slate-500">Biaya tambahan</span><b>{formatIDR(t.extra)}</b></div>
        <div className="flex justify-between border-t border-slate-200 pt-1.5 text-sm"><span className="font-bold text-slate-800">Total HPP per pcs</span><b data-testid="costf-total-val" className="text-[#7F1D2D]">{formatIDR(t.total)}</b></div>
        {form.product_id && selVariant && <div className="flex justify-between text-[10px] text-slate-400"><span>Harga Beli saat ini (Produk Utama)</span><span>{formatIDR(currentCost ?? 0)} — bukan sumber perhitungan HPP</span></div>}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button data-testid="costf-draft" onClick={saveDraft} disabled={busy} className="h-11 rounded-xl border border-[#7F1D2D]/40 text-[#7F1D2D] text-sm font-semibold disabled:opacity-40">Simpan Draft</button>
        <button data-testid="costf-sync" onClick={saveAndSync} disabled={busy} className="h-11 rounded-xl text-white text-sm font-semibold disabled:opacity-40" style={{ background: MAROON }}>Simpan & Update Harga Beli</button>
      </div>

      {confirm && (
        <div data-testid="costf-confirm" className="border-2 border-[#7F1D2D]/30 rounded-xl p-4 space-y-2 bg-[#FBF3F4]/50">
          <div className="text-xs font-bold text-slate-800">Konfirmasi Update Harga Beli</div>
          <div className="text-xs text-slate-600 space-y-1">
            <div>Tujuan: <b>{confirm.product?.name}</b> • {confirm.variants.length} variasi</div>
            {confirm.variants.map((v) => (
              <div key={v.label}>• {v.label}: <b>{formatIDR(v.oldPrice)}</b> → <b className="text-[#7F1D2D]">{formatIDR(confirm.newPrice)}</b></div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="costf-confirm-no" onClick={() => setConfirm(null)} className="h-10 rounded-xl border border-slate-200 text-slate-500 text-xs font-semibold">Batal</button>
            <button data-testid="costf-confirm-yes" onClick={doSync} disabled={busy} className="h-10 rounded-xl text-white text-xs font-semibold disabled:opacity-40" style={{ background: MAROON }}>{busy ? "Memproses..." : "Ya, Update Harga Beli"}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function CostDetail({ cost, onClose, onReload, canMaster }) {
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("rincian");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/admin/prod/costs/${cost.id}`).then((r) => setDetail(r.data)).catch(() => {}); }, [cost.id]);
  const d = detail?.cost || cost;
  const retry = async () => {
    setBusy(true);
    try { await api.post(`/admin/prod/costs/${cost.id}/sync`); toast.success("Harga Beli berhasil diperbarui"); onReload?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Sinkronisasi gagal"); onReload?.(); }
    finally { setBusy(false); }
  };
  return (
    <Modal tid="costd-modal" title={`${d.code} • ${d.name}`} onClose={onClose} wide>
      <div className="flex gap-1 border-b border-slate-200">
        {[["rincian", "Rincian Biaya"], ["produksi", "Riwayat Produksi"], ["perubahan", "Riwayat Perubahan"]].map(([k, lb]) => (
          <button key={k} data-testid={`costd-tab-${k}`} onClick={() => setTab(k)}
            className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px ${tab === k ? "border-[#7F1D2D] text-[#7F1D2D] font-semibold" : "border-transparent text-slate-500"}`}>{lb}</button>
        ))}
      </div>
      {tab === "rincian" && (
        <div className="space-y-2 text-xs" data-testid="costd-rincian">
          <div className="text-[11px] text-slate-500">Terhubung ke <b>{d.product_name}</b> • variasi <b>{d.variant}</b> • status <b>{d.status}</b></div>
          {(d.materials || []).map((m, i) => <div key={i} className="flex justify-between"><span>{m.name} — {m.usage_per_pcs} {m.unit} × {formatIDR(m.price_per_unit)}</span><b>{formatIDR(m.subtotal)}</b></div>)}
          <div className="flex justify-between"><span>Ongkos potong / jahit / finishing</span><b>{formatIDR(d.cost_potong)} / {formatIDR(d.cost_jahit)} / {formatIDR(d.cost_finishing)}</b></div>
          {(d.consumables || []).map((c, i) => <div key={i} className="flex justify-between"><span>{c.name} — {c.qty} {c.unit} × {formatIDR(c.price_per_unit)}</span><b>{formatIDR(c.subtotal)}</b></div>)}
          {(d.extras || []).map((e, i) => <div key={i} className="flex justify-between"><span>{e.name}</span><b>{formatIDR(e.amount)}</b></div>)}
          <div className="flex justify-between border-t border-slate-200 pt-2 text-sm"><span className="font-bold">Total HPP/pcs</span><b className="text-[#7F1D2D]">{formatIDR(d.total_hpp)}</b></div>
          <div className="flex justify-between text-[11px] text-slate-500"><span>Harga Beli Produk Utama saat ini</span><b>{formatIDR(cost.current_cost_price ?? 0)}</b></div>
          <div className="flex items-center justify-between">
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${SYNC_CHIP[d.sync_status]}`}>{SYNC_LABEL[d.sync_status]}</span>
            {canMaster && d.sync_status === "belum_tersinkron" && <button data-testid="costd-retry" onClick={retry} disabled={busy} className="h-8 px-3 rounded-lg text-white text-[11px] font-semibold flex items-center gap-1 disabled:opacity-40" style={{ background: MAROON }}><RefreshCw className="w-3 h-3" /> Coba Lagi</button>}
          </div>
          {d.sync_error && <p className="text-[10px] text-red-500">{d.sync_error}</p>}
          {d.note && <p className="text-[11px] text-slate-500 bg-slate-50 rounded-lg p-2">{d.note}</p>}
        </div>
      )}
      {tab === "produksi" && (
        <div className="space-y-2" data-testid="costd-produksi">
          <p className="text-[10px] text-slate-400">Estimasi ongkos (potong+jahit+finishing) vs upah aktual batch. Biaya aktual batch TIDAK otomatis menimpa Harga Beli.</p>
          {(detail?.batches || []).map((b) => (
            <div key={b.id} className="border border-slate-100 rounded-xl p-3 text-xs">
              <div className="flex justify-between"><span className="font-mono font-bold">{b.number}</span><span className="text-slate-400">{formatDateID(b.created_at)}</span></div>
              <div className="text-slate-500 mt-1">{b.total_qty?.toLocaleString("id-ID")} pcs • estimasi ongkos {formatIDR(b.wage_estimate_per_pcs)}/pcs • upah aktual {formatIDR(b.wage_actual_per_pcs)}/pcs (total {formatIDR(b.wage_actual)})</div>
            </div>
          ))}
          {detail && !detail.batches.length && <p className="text-center text-xs text-slate-400 py-6">Belum ada batch produksi untuk produk/variasi ini.</p>}
        </div>
      )}
      {tab === "perubahan" && (
        <div className="space-y-2" data-testid="costd-perubahan">
          {(detail?.price_history || []).map((h) => (
            <div key={h.id} className="border border-slate-100 rounded-xl p-3 text-xs flex items-center gap-2">
              <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
              <div>Harga Beli <b>{formatIDR(h.old_price ?? 0)}</b> → <b className="text-[#7F1D2D]">{formatIDR(h.new_price)}</b><div className="text-[10px] text-slate-400">{h.by} • {formatDateID(h.created_at)}</div></div>
            </div>
          ))}
          {detail && !detail.price_history.length && <p className="text-center text-xs text-slate-400 py-6">Belum ada perubahan harga.</p>}
        </div>
      )}
    </Modal>
  );
}

export default function AdminProductionCosts() {
  const { hasPerm } = useAuth();
  const canMaster = hasPerm("production.master");
  const [items, setItems] = useState(null);
  const [options, setOptions] = useState({ products: [], materials: [], units: [] });
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [detailFor, setDetailFor] = useState(null);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});

  const load = useCallback(() => {
    api.get("/admin/prod/costs").then((r) => setItems(r.data)).catch((e) => { setItems([]); toast.error(e?.response?.data?.detail || "Gagal memuat produk produksi"); });
    api.get("/prod/options").then((r) => setOptions(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (items || []).filter((c) => !s || c.name.toLowerCase().includes(s) || c.code.toLowerCase().includes(s) || (c.product_name || "").toLowerCase().includes(s));
  }, [items, q]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const c of filtered) {
      const key = c.product_id || c.id;
      if (!map.has(key)) map.set(key, { key, product_name: c.product_name || c.name, items: [] });
      map.get(key).items.push(c);
    }
    return [...map.values()].map((g) => ({
      ...g,
      synced: g.items.filter((c) => c.sync_status === "tersinkron").length,
      pending: g.items.filter((c) => c.sync_status === "belum_tersinkron").length,
      drafts: g.items.filter((c) => c.sync_status === "draft").length,
      hppMin: Math.min(...g.items.map((c) => c.total_hpp)),
      hppMax: Math.max(...g.items.map((c) => c.total_hpp)),
    }));
  }, [filtered]);
  const { paged: pagedGroups, pager: costsPager } = usePager(grouped, 10);

  const toggle = async (c) => {
    try { await api.post(`/admin/prod/costs/${c.id}/toggle`); toast.success("Status diubah"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah status"); }
  };

  return (
    <div data-testid="prod-costs-page" className="space-y-5 pb-10">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Produk Produksi</h1>
          <p className="text-xs text-slate-400 mt-0.5">Master biaya & HPP per pcs — terhubung ke Produk Utama via ID, tanpa membuat ulang produk. Sinkron: HPP → Harga Beli.</p>
        </div>
        {canMaster && <button data-testid="costs-create-btn" onClick={() => { setEditItem(null); setShowForm(true); }} className="h-10 px-4 rounded-xl text-white text-sm font-semibold flex items-center gap-1.5" style={{ background: MAROON }}><Plus className="w-4 h-4" /> Tambah Produk Produksi</button>}
      </div>

      <div className="relative max-w-sm">
        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="costs-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari kode / nama / produk utama" className={`${inpL} pl-9`} />
      </div>

      <div className={`${card} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase text-slate-400 border-b border-slate-100 bg-slate-50/60">
                <th className="px-4 py-3">Kode</th><th className="px-3 py-3">Nama</th><th className="px-3 py-3">Variasi</th>
                <th className="px-3 py-3 text-right">Kain</th><th className="px-3 py-3 text-right">Potong</th><th className="px-3 py-3 text-right">Jahit</th><th className="px-3 py-3 text-right">Finishing</th><th className="px-3 py-3 text-right">Consumable</th>
                <th className="px-3 py-3 text-right">Total HPP</th><th className="px-3 py-3 text-right">Harga Beli</th><th className="px-3 py-3">Sinkron</th><th className="px-4 py-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {pagedGroups.map((g) => (
                <Fragment key={g.key}>
                  <tr data-testid={`costs-group-${g.key}`} onClick={() => setExpanded((e) => ({ ...e, [g.key]: !e[g.key] }))}
                    className="border-b border-slate-100 bg-slate-50/40 hover:bg-rose-50/30 cursor-pointer">
                    <td colSpan={12} className="px-4 py-3">
                      <div className="flex items-center gap-3 flex-wrap">
                        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded[g.key] ? "" : "-rotate-90"}`} />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-slate-800 text-[13px] truncate">{g.product_name}</div>
                          <div className="text-[10px] text-slate-400">{g.items.length} variasi • Total HPP {g.hppMin === g.hppMax ? formatIDR(g.hppMin) : `${formatIDR(g.hppMin)} – ${formatIDR(g.hppMax)}`} per pcs</div>
                        </div>
                        {g.synced > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-emerald-50 text-emerald-600">{g.synced} Tersinkron</span>}
                        {g.drafts > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-slate-100 text-slate-500">{g.drafts} Draft</span>}
                        {g.pending > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-red-50 text-red-600">{g.pending} Belum Tersinkron</span>}
                      </div>
                    </td>
                  </tr>
                  {expanded[g.key] && g.items.map((c) => (
                    <tr key={c.id} data-testid={`costs-row-${c.id}`} className="border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer" onClick={() => setDetailFor(c)}>
                      <td className="pl-9 pr-4 py-2.5 font-mono font-bold text-slate-700">{c.code}{c.status === "nonaktif" && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 font-sans">nonaktif</span>}</td>
                      <td className="px-3 py-2.5 font-semibold text-slate-800">{c.name}</td>
                      <td className="px-3 py-2.5 text-slate-600">{c.variant}</td>
                      <td className="px-3 py-2.5 text-right">{formatIDR(c.totals.fabric)}</td>
                      <td className="px-3 py-2.5 text-right">{formatIDR(c.totals.potong)}</td>
                      <td className="px-3 py-2.5 text-right">{formatIDR(c.totals.jahit)}</td>
                      <td className="px-3 py-2.5 text-right">{formatIDR(c.totals.finishing)}</td>
                      <td className="px-3 py-2.5 text-right">{formatIDR(c.totals.consumable + c.totals.extra)}</td>
                      <td className="px-3 py-2.5 text-right font-bold text-[#7F1D2D]">{formatIDR(c.total_hpp)}</td>
                      <td className="px-3 py-2.5 text-right text-slate-600">{formatIDR(c.current_cost_price ?? 0)}</td>
                      <td className="px-3 py-2.5"><span data-testid={`costs-sync-${c.id}`} className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${SYNC_CHIP[c.sync_status]}`}>{SYNC_LABEL[c.sync_status]}</span></td>
                      <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                        {canMaster && (
                          <div className="flex gap-1.5 justify-end">
                            <button data-testid={`costs-edit-${c.id}`} onClick={() => { setEditItem(c); setShowForm(true); }} className="h-7 px-2.5 rounded-lg border border-slate-200 text-[10px] font-semibold text-slate-500">Edit</button>
                            <button data-testid={`costs-toggle-${c.id}`} onClick={() => toggle(c)} className="h-7 w-7 rounded-lg border border-slate-200 text-slate-400 flex items-center justify-center"><Power className="w-3 h-3" /></button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
              {items && !grouped.length && <tr><td colSpan={12} data-testid="costs-empty" className="py-10 text-center text-slate-400">Belum ada Produk Produksi.</td></tr>}
              {!items && <tr><td colSpan={12} className="py-10 text-center text-slate-400">Memuat...</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <Pager {...costsPager} testid="costs-pager" />

      {showForm && <CostForm initial={editItem} options={options} onClose={() => setShowForm(false)} onSaved={load} />}
      {detailFor && <CostDetail cost={detailFor} onClose={() => setDetailFor(null)} onReload={() => { setDetailFor(null); load(); }} canMaster={canMaster} />}
    </div>
  );
}
